import React from 'react'
import Image from 'gatsby-image'
import styled, { css } from 'styled-components'
import { up, down } from '@thomaseckhardt/styled-breakpoints'
import { Box } from 'rebass'
import Topline from '../components2/Topline'
import Headline from '../components2/Headline'
import Subline from '../components2/Subline'
import Lead from '../components2/Lead'
import Copytext from '../components2/Copytext'
import Quote from '../components2/Quote'
import Puzzle from '../components2/Puzzle'
import Laces from '../components2/Laces'
import ButtonGroup from '../components2/ButtonGroup'
import Button from '../components2/Button'

export const VARIANT_OPENER = 'opener'
export const VARIANT_TEXT_INTENDED = 'text-intended'
export const VARIANT_TEXT_CENTERED = 'text-centered'
export const VARIANT_IMAGE_TEXT = 'image-text'
export const ARTICLE_VARIANT_TRIO = 'trio'
export const VARIANT_TEXT_ALIGN_CENTERED_NARROW = 'text-align-centered-narrow'

// @NEXT: https://varun.ca/styled-system/
// https://medium.com/@shivapandey04/styled-components-styled-system-super-power-part-ii-2225879ca4f8
// https://www.npmjs.com/package/babel-plugin-styled-components
// https://github.com/gregoralbrecht/gatsby-starter-styled/blob/82f65d29194047d1defddee8509d219c776f2176/src/util/style/index.js
// https://www.npmjs.com/package/eslint-config-airbnb

/**
 * intro: headline, lead required
 * intro-image: headline, lead, image required
 * text
 * text-indented
 * text-centered
 * image-text
 */

const Root = styled(Box)`
  position: relative;
  margin: 0 auto;
  max-width: var(--content-max-width);
  ${props =>
    props.leading > 0 &&
    css`
      padding-top: ${props =>
        props.theme.px(`space.${props.leading}`, `0`)(props)};
    `}
`

const Content = styled(Box)`
  display: grid;
  grid-template-columns: repeat(16, 1fr);
  grid-template-rows: 1fr;
  grid-row-gap: ${props => props.theme.px('space.3')(props)};
`

const Text = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-column: 2 / -2;
  grid-row-start: 1;

  /* VARIANT_OPENER */
  ${props =>
    props.variant === VARIANT_OPENER &&
    css`
      max-width: 576px;

      ${up('tablet')} {
        grid-column: 2 / span 6;
      }

      ${up('tabletLarge')} {
        grid-column: 2 / span 5;
      }
    `}
  
  /* VARIANT_TEXT_INTENDED */
  ${props =>
    props.variant === VARIANT_TEXT_INTENDED &&
    css`
      max-width: 640px;

      ${up('tablet')} {
        grid-column: 3 / span 12;
      }

      ${up('laptop')} {
        grid-column: 5 / span 12;
      }
    `}
  
  /* VARIANT_TEXT_CENTERED */
  ${props =>
    props.variant === VARIANT_TEXT_CENTERED &&
    css`
      margin-left: auto;
      margin-right: auto;
    `}
  
  /* VARIANT_TEXT_ALIGN_CENTERED_NARROW */
  ${props =>
    props.variant === VARIANT_TEXT_ALIGN_CENTERED_NARROW &&
    css`
      text-align: center;
      max-width: 640px;
      margin-left: auto;
      margin-right: auto;
    `}
  
  /* VARIANT_IMAGE_TEXT */
  ${props =>
    props.variant === VARIANT_IMAGE_TEXT &&
    css`
      max-width: 576px;

      ${up('tablet')} {
        grid-column: 7 / -2;
        padding-left: ${props => props.theme.px('space.3')};
      }
    `}
  
  /* VARIANT_TRIO */
  ${props =>
    props.variant === ARTICLE_VARIANT_TRIO &&
    css`
      margin-left: auto;
      margin-right: auto;
    `}

  & > *:first-child {
    margin-top: 0;
  }
`

// grid-column: ${props => `${props.mediaStart} / span ${props.mediaWidth}`};
const MediaContainer = styled(Box)`
  grid-column: 1 / -1;

  /* VARIANT_OPENER */
  ${props =>
    props.variant === VARIANT_OPENER &&
    css`
      ${up('tablet')} {
        grid-column: 8 / -2;
        grid-row-start: 1;
        padding-left: ${props => props.theme.px('space.3')};
      }
    `}

  /* VARIANT_TEXT_INTENDED */
  ${props =>
    props.variant === VARIANT_TEXT_INTENDED &&
    css`
      ${up('tablet')} {
        grid-column: 3 / span 12;
      }

      ${up('laptop')} {
        grid-column: 5 / span 11;
      }
    `}
  
  /* VARIANT_IMAGE_TEXT */
  ${props =>
    props.variant === VARIANT_IMAGE_TEXT &&
    css`
      grid-column: 2 / -2;

      ${up('tablet')} {
        grid-column: 2 / span 5;
      }

      ${up('laptop')} {
        grid-column: 3 / span 4;
      }
    `}
`

const StyledPuzzle = styled(Puzzle)`
  grid-column: 1 / span 16;
`

const Heading = styled(Box)`
  position: relative;
`

const StyledQuote = styled(Quote)`
  position: relative;
`

const StyledLaces = styled(Laces)`
  width: ${(1 / 5) * 100}%;

  &:not(:first-of-type) {
    margin-top: 0;
  }

  ${down('tabletLarge')} {
    display: none;
  }

  ${up('laptop')} {
    width: ${(3 / 5) * 100}%;
  }
`

const Actions = styled(Box)`
  position: relative;
`

const Article = props => {
  const hasTopline = 'topline' in props
  const hasHeadline = 'headline' in props
  const hasSubline = 'subline' in props
  const hasLead = 'lead' in props
  const hasQuote = 'quote' in props
  const hasCopytext = 'copytext' in props
  const hasText = hasHeadline || hasLead || hasCopytext || hasQuote
  const hasMedia = props.image || props.video
  const hasPuzzle = props.puzzle
  const hasImage = 'image' in props
  const hasLaces = props.laces && props.variant === VARIANT_OPENER
  const hasActions = props.actions && props.actions.length

  const actionElems = []
  if (hasActions) {
    props.actions.forEach((item, index) => {
      const { label, ...rest } = item
      actionElems.push(
        <Button key={index} {...rest}>
          {label}
        </Button>
      )
    })
  }

  return (
    <Root {...props}>
      <Content>
        {hasText && (
          <Text variant={props.variant}>
            {hasHeadline && (
              <Heading variant={props.variant} mt={2}>
                {hasTopline && <Topline mb={1}>{props.topline}</Topline>}
                {hasHeadline && (
                  <Headline headlineSize={props.headlineSize}>
                    {props.headline}
                  </Headline>
                )}
                {hasSubline && <Subline>{props.subline}</Subline>}
              </Heading>
            )}
            {hasLead && (
              <Lead mt={2} forwardedAs="p">
                {props.lead}
              </Lead>
            )}
            {hasQuote && (
              <StyledQuote mt={2} forwardedAs="p">
                {props.quote}
              </StyledQuote>
            )}
            {hasCopytext && (
              <Copytext mt={2} dropcap={props.dropcap}>
                {props.copytext}
              </Copytext>
            )}
            {hasLaces && <StyledLaces mt={2} />}
            {hasActions && (
              <Actions mt={4}>
                <ButtonGroup>{actionElems}</ButtonGroup>
              </Actions>
            )}
          </Text>
        )}
        {hasMedia && (
          <MediaContainer
            offset={`${24 * (props.mediaStart < props.textStart ? -1 : 1)}px`}
            variant={props.variant}
            mediaStart={props.mediaStart}
            mediaWidth={props.mediaWidth}
          >
            {hasImage && (
              <Image fluid={props.image.fluid} backgroundColor={'#f5f5f5'} />
            )}
          </MediaContainer>
        )}
        {hasPuzzle && <StyledPuzzle {...props.puzzle} />}
      </Content>
    </Root>
  )
}

Article.defaultProps = {
  leading: 9,
  dropcap: false,
  textStart: 3,
  textWidth: 4,
  headlineSize: 2,
  mediaStart: 1,
  mediaWidth: 16,
  variant: VARIANT_TEXT_INTENDED,
}

export default Article
